import { Box, FormControl, FormControlLabel, MenuItem, Radio, RadioGroup } from "@mui/material";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import getLocalString from "constants/Localization";
import MDTypography from "components/MDTypography";
import { DeleteOpenProposalAction, User } from "generated/graphql";
import { GlobalMessage } from "hooks/notifications/useGlobalMessage";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import useDeleteOrganizationUser from "hooks/users/useDeleteOrganizationUser";
import useGetOrganizationUser from "hooks/users/useGetOrganizationUser";
import PickActiveOrganizationUser from "modules/PickActiveOrganizationUser/PickActiveOrganizationUser";
import { useCallback, useEffect, useState } from "react";

export default function DeleteAccountMenuItem({
  closeMenu,
  id,
}: {
  closeMenu: () => void;
  id: User["id"];
}) {
  // As soon as this module loads we need to figure out if this user has any proposal count
  const [getOrganizationUser, { user, loading, error }] = useGetOrganizationUser();

  useEffect(() => {
    getOrganizationUser({ id });
  }, []);

  const [deleteOrganizationUser, { isSuccess }] = useDeleteOrganizationUser();
  const [openProposalAction, setOpenProposalAction] = useState<DeleteOpenProposalAction>(
    DeleteOpenProposalAction.CANCEL
  );

  type ReassignType = { id?: User["id"]; label?: string };
  const [reassignUserId, setReassignUserId] = useState<ReassignType>({});
  const [reassignJobUser, setReassignJobUser] = useState<ReassignType>({});
  const [reassignJobPhaseForemanUser, setReassignJobPhaseForemanUser] = useState<ReassignType>({});

  const acceptFn = useCallback(async (): Promise<GlobalMessage> => {
    const { data } = await deleteOrganizationUser({
      id,
      openProposalAction,
      ...(reassignUserId.id && { reassignUserId: reassignUserId.id }),
      ...(reassignJobUser.id && { reassignJobUserId: reassignJobUser.id }),
      ...(reassignJobPhaseForemanUser.id && {
        reassignJobPhaseForemanUserId: reassignJobPhaseForemanUser.id,
      }),
    });
    if (isSuccess(data)) {
      closeMenu();
      return { message: getLocalString("organization.archive-user-success") };
    } else {
      return {
        message: getLocalString("organization.archive-user-failure"),
        options: { variant: "error" },
      };
    }
  }, [
    deleteOrganizationUser,
    id,
    openProposalAction,
    reassignUserId,
    reassignJobUser,
    reassignJobPhaseForemanUser,
  ]);

  const cancelFn = useCallback(async () => {
    closeMenu();
    return true;
  }, []);

  const { open, setOpen, onAccept, onCancel } = useConfirmationDialog({
    acceptFn,
    cancelFn,
  });

  const reassignUserMissing = !reassignUserId?.id;
  const wantsToReassign = openProposalAction === DeleteOpenProposalAction.REASSIGN;
  const requiresProposalAction = user && user.proposalCount > 0;
  const requiresJobAction = user?.jobsCount > 0;
  const requiresJobPhaseAction = user?.jobPhasesCount > 0;
  const missingProposalAction = openProposalAction === null;
  const isAcceptDisabled =
    loading ||
    (requiresProposalAction && missingProposalAction) ||
    (requiresProposalAction && wantsToReassign && !reassignUserId?.id) ||
    (requiresJobAction && !reassignJobUser.id) ||
    (requiresJobPhaseAction && !reassignJobPhaseForemanUser.id);
  const isCancelDisabled = false;

  const requiresProposalOrJobAction =
    requiresProposalAction || requiresJobAction || requiresJobPhaseAction;

  return (
    <>
      <ActionConfirmDialog
        open={open}
        setOpen={setOpen}
        handleAccept={onAccept}
        handleCancel={onCancel}
        title="Confirmation"
        isAcceptDisabled={isAcceptDisabled}
        isCancelDisabled={isCancelDisabled}
        content={
          requiresProposalOrJobAction ? (
            <Box>
              {requiresProposalAction && (
                <Box mb={3}>
                  <MDTypography variant="body2" fontWeight="medium" mb={2}>
                    This user has {user?.proposalCount} proposals assigned to them. Please specify
                    what to do with them:
                  </MDTypography>
                  <FormControl>
                    <RadioGroup
                      aria-labelledby="open-proposals-actions"
                      name="open-proposals-action"
                      onChange={(e, value: DeleteOpenProposalAction) =>
                        setOpenProposalAction(value)
                      }
                    >
                      <FormControlLabel
                        value={DeleteOpenProposalAction.REASSIGN}
                        control={<Radio size="small" />}
                        label={
                          <MDTypography variant="body2" fontWeight="regular">
                            Reassign to another user
                          </MDTypography>
                        }
                      />
                      <FormControlLabel
                        value={DeleteOpenProposalAction.CANCEL}
                        control={<Radio size="small" sx={{ mt: -0.5 }} />}
                        sx={{ alignItems: "flex-start" }}
                        label={
                          <Box>
                            <MDTypography variant="body2" fontWeight="regular">
                              Cancel proposals
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              Canceling proposals restricts customers from viewing and signing them.
                            </MDTypography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                  {wantsToReassign && (
                    <Box mt={2}>
                      <PickActiveOrganizationUser
                        onChange={(e, value) => setReassignUserId(value)}
                        value={reassignUserId}
                      />
                    </Box>
                  )}
                </Box>
              )}
              {requiresJobAction && (
                <Box mb={3}>
                  <MDTypography variant="body2" fontWeight="medium">
                    This user has {user?.jobsCount} job(s) assigned to them. Please select a user to
                    reassign them to:
                  </MDTypography>
                  <PickActiveOrganizationUser
                    onChange={(e, value) => setReassignJobUser(value)}
                    value={reassignJobUser}
                    label="Select a user to reassign the jobs to"
                    sx={{ my: 0 }}
                  />
                </Box>
              )}
              {requiresJobPhaseAction && (
                <Box mb={3}>
                  <MDTypography variant="body2" fontWeight="medium">
                    This user has {user?.jobPhasesCount} job phase(s) assigned to them. Please
                    select a user to reassign them to:
                  </MDTypography>
                  <PickActiveOrganizationUser
                    onChange={(e, value) => setReassignJobPhaseForemanUser(value)}
                    value={reassignJobPhaseForemanUser}
                    label="Select a user to reassign the job phases to"
                    sx={{ my: 0 }}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <MDTypography variant="body2">
              This user will be archived and removed from active lists, dropdowns, and assignment
              pickers, and their subscription seat (if applicable) will be freed. Their data is
              preserved and you can restore them later from Archived Users. Are you sure you want to
              archive this user?
            </MDTypography>
          )
        }
      />
      <MenuItem onClick={() => setOpen(true)}>Archive Account</MenuItem>
    </>
  );
}
